import { List } from 'immutable';
import keyMirror from 'keymirror';

import config from '../config';
import { OrderTripTypes } from '../constants/AppConstants';
import trainTime from '../lib/TrainTime';

const StandardClaimWindowDays = config.get('delayRepayMaxTicketDays');

/**
 * @typedef {import('../records/Order')} Order
 * @typedef {import('../records/OrderTrip')} OrderTrip
 * @typedef {import('../records/OrderFare')} OrderFare
 * @typedef {import('../records/OrderJourney')} OrderJourney
 */

export const HandoffState = keyMirror({
    UNSUPPORTED_TRIP: null,
    REFUNDED_FARE: null,
    OUTSIDE_CLAIM_WINDOW: null,
    NO_ACTIVE_TICKETS: null,
    NO_VALID_TOC: null,
    CLAIMABLE: null,
});

/**
 * Get the handoff state for a journey
 * @param {Order} order
 * @param {OrderFare} fare
 * @param {OrderTrip} trip
 * @param {OrderJourney} journey
 * @param {String[]} tocGroup
 * @returns {keyof HandoffState}
 */
export function getJourneyState(order, trip, fare, journey, tocGroup = []) {
    if (!journey) {
        return null;
    }

    let status;
    if (tripIsUnsupported(trip)) {
        status = HandoffState.UNSUPPORTED_TRIP;
    } else if (!hasLiveTickets(order, fare)) {
        status = HandoffState.NO_ACTIVE_TICKETS;
    } else if (!isJourneyWithinClaimWindow(journey)) {
        status = HandoffState.OUTSIDE_CLAIM_WINDOW;
    } else if (!isJourneyOnSupportedToc(journey, tocGroup)) {
        status = HandoffState.NO_VALID_TOC;
    } else {
        status = HandoffState.CLAIMABLE;
    }

    return status;
}

/**
 * Get the handoff status for each journey in the order.
 * @param {Order} order
 * @param {String[]} tocGroup
 * @returns {List<{trip: OrderTrip, fare: OrderFare, journey: OrderJourney, status: keyof HandoffState}>}
 */
export function validateOrderJourneys(order, tocGroup = []) {
    const mergeJourneyState = (trip, fare, journey) => {
        const status = getJourneyState(order, trip, fare, journey, tocGroup);
        return status && { trip, fare, journey, status };
    };

    // Create a new List of tuples: [OrderTrip, OrderFare, OrderJourney, status]
    const fareJourneys = order.trips.flatMap(trip =>
        trip
            .getJourneyFares()
            .flatMap(fare =>
                List.of(
                    mergeJourneyState(trip, fare, fare.outwardJourney),
                    mergeJourneyState(trip, fare, fare.returnJourney),
                ).filter(Boolean),
            ),
    );

    return fareJourneys;
}

/**
 * Returns true if the order trip is for an unsupported type (e.g. season, daysave, ...)
 * @param {OrderTrip} trip
 * @return {boolean}
 */
export function tripIsUnsupported(trip) {
    // Return false for open return and other trip types
    const notApplicable = [OrderTripTypes.SEASON, OrderTripTypes.DAYSAVE, OrderTripTypes.ROVERSRANGERS];
    return trip.isOpenReturn() || notApplicable.includes(trip.getType());
}

/**
 * Returns true if the ticket is not refunded
 * @param {Order} order
 * @param {OrderTicket} ticket
 * @returns {boolean}
 */
export function ticketIsLive(order, ticket) {
    return (
        !ticket.refunded &&
        (order.isETicket() || ticket.getTicketNumber() || ticket.getSmartcardNumber())
    );
}

/**
 * Returns true if there are tickets which are not refunded
 * @param {OrderOrder} order
 * @param {OrderFare} fare
 * @returns {boolean}
 */
export function hasLiveTickets(order, fare) {
    return fare && fare.tickets.some(ticket => ticketIsLive(order, ticket));
}

/**
 * Returns true if the journey departed within the valid 28-day claim window
 * @param {OrderJourney} journey
 * @return {boolean}
 */
export function isJourneyWithinClaimWindow(journey) {
    const departure = journey && journey.getDepartureTime();
    return (
        departure &&
        departure.isBefore() &&
        departure.isAfter(
            trainTime()
                .startOf('day')
                .subtract(StandardClaimWindowDays, 'days'),
        )
    );
}

/**
 * Returns true if there journey has legs on at least one specified TOC
 * @param {OrderJourney} journey
 * @param {String[]} tocGroup TOC short code
 * @return {boolean}
 */
export function isJourneyOnSupportedToc(journey, tocGroup = []) {
    const legs = journey.legs.filter(leg => leg.toc);
    return legs.some(leg => leg.toc && tocGroup.includes(leg.toc.code));
}



// WEBPACK FOOTER //
// ./src/helpers/DelayRepayOrderHandoff.js